/* eslint-disable no-unreachable */
import React, {
  useState,
  useEffect,
  useMemo,
  useContext,
  useCallback
} from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Title, Tab, Field, Control, Button, Icon, Tag } from "rbx";
import _ from "lodash";
import moment from "moment";
import { isSameDay, endOfDay } from "date-fns";
import { useQuery, useMutation, useApolloClient } from "react-apollo";
import {
  Route,
  useParams,
  useLocation,
  useHistory,
  Switch
} from "react-router-dom";
import "./WorkOrder.scss";

import {
  uuidGenerator,
  formatCurrency,
  unmaskCurrency,
  getUTCFormattedDate,
  getDaysDiffBetweenDates,
  ourToast as toast
} from "../../../utils";
import {
  useCurrentUser,
  useCompanyParameters,
  useSystemCodes
} from "../../../utils/hooks";
import { useOdometerRequired } from "../../../hooks";
import Loading from "../../../components/Loading";

import {
  CREATE_WORK_ORDER_MUTATION,
  UPDATE_WORK_ORDER_MUTATION,
  DELETE_WORK_ORDER_MUTATION,
  SINGLE_WORK_ORDER_QUERY
} from "../../../graphql/WorkOrders/WorkOrders.graphql";

import {
  ALL_VEHICLE_OUT_OF_SERVICES_QUERY,
  ALL_VEHICLE_INSPECTIONS_MASTER_QUERY,
  CREATE_VEHICLE_INSPECTION_MUTATION,
  ALL_VEHICLE_INSPECTIONS_QUERY,
  SINGLE_VEHICLE_QUERY
} from "../../../graphql/Vehicles/Vehicles.graphql";

import { ALL_INSPECTIONS_QUERY } from "../../../graphql/Inspections/Inspections.graphql";

import {
  ALL_WORK_ORDER_LABORS_QUERY,
  DELETE_WORK_ORDER_LABOR_MUTATION,
  UPDATE_ALL_WORK_ORDER_LABORS_MUTATION
} from "../../../graphql/WorkOrders/WorkOrderLabors.graphql";

import {
  ALL_WORK_ORDER_PARTS_QUERY,
  DELETE_WORK_ORDER_PART_MUTATION,
  UPDATE_ALL_WORK_ORDER_PARTS_MUTATION
} from "../../../graphql/WorkOrders/WorkOrderParts.graphql";
import { SINGLE_ITEM_CATEGORY_QUERY } from "../../../graphql/ItemCategories/ItemCategories.graphql";

import { ALL_INVENTORY_ON_HAND_QUERY } from "../../../graphql/Items/InventoryOnHand.graphql";

import { ALL_SYSTEM_CODES_QUERY } from "../../../graphql/SystemCodes/SystemCodes.graphql";

import { SINGLE_EMPLOYEE_QUERY } from "../../../graphql/Employees/Employees.graphql";

import MainInformation from "./routes/MainInformation";
import Labor from "./routes/Labor";
import Part from "./routes/Part";

import { ModalContext } from "../../../components/ModalProvider";

import { Confirmation } from "../../../components/modals";

import { VoidWorkOrderModal } from "../../../components/VoidWorkOrder";

import { useAuthorization } from "../../../components/Authorization";

import { VehicleOutOfServiceModal } from "../../../components/VehicleOutOfService";

import { useCanPostWorkOrder } from "./utils/useCanPostWorkOrder.js";

class OdometerError extends Error {}

const INITIAL_STATE = {
  RecId: null,
  VehicleRecIdRef: null,
  Odometer: null,
  ItemCatgRecIdRef: null,
  WODate: null,
  CompletedDate: null,
  Billable: false,
  Notes: null,
  LocRecIdRef: null,
  WOTypeRecIdRef: null,
  MechanicRecIdRef: null,
  WOStatusRecIdRef: null,
  LaborApproved: null,
  PartsApproved: false,
  Active: true,
  PostedDt: null,
  PostedBy: null,
  ApprovedBy: null,
  ApprovalDt: null,
  WorkOrder: null,
  Status: null,
  VendorRecIdRef: null,
  Vendor: null,
  VehicleTypeRecIdRef: null
};

const WorkOrderPage = () => {
  const SystemCodes = useSystemCodes({
    Category: "Fleet",
    CodeName: "WOStatus",
    CodeId: "ENT"
  });

  const { data: inspectionServiceSystemCodeData } = useSystemCodes({
    Category: "Fleet",
    CodeName: "WOType",
    CodeDescription: "Inspection Service"
  });

  const VSSystemCode = useSystemCodes({
    Category: "Fleet",
    CodeName: "WOType",
    CodeId: "VS"
  });

  const canPost = useAuthorization(["ADMIN", "FLEET"]);
  const isMechanic = useAuthorization(["MECH"]);
  const { id } = useParams();
  const history = useHistory();
  const client = useApolloClient();
  const location = useLocation();
  const { data: user } = useCurrentUser();
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(true);
  const [editing, setEditing] = useState(
    location.pathname.includes("main-information") ||
      location.pathname.includes("parts") ||
      location.pathname.includes("labor")
  );

  const { REACT_APP_REPORT_BASE_URL = "https://gdev.gravoc.com" } = process.env;

  const BASE_URL = `${REACT_APP_REPORT_BASE_URL}/ReportViewer/ReportViewer.aspx?Path=Work%20Order%20Print%20Doc`;

  const { data: companySettings = {} } = useCompanyParameters();

  const { setModalOpen } = useContext(ModalContext);
  const workOrderData = useQuery(SINGLE_WORK_ORDER_QUERY, {
    variables: { where: { RecId: parseInt(id) } }
  });
  const workOrderLaborsData = useQuery(ALL_WORK_ORDER_LABORS_QUERY, {
    variables: {
      where: {
        WorkOrderRecIdRef: parseInt(id)
      }
    },
    fetchPolicy: "cache-and-network"
  });

  const workOrderPartsData = useQuery(ALL_WORK_ORDER_PARTS_QUERY, {
    variables: {
      where: {
        WorkOrderRecIdRef: parseInt(id)
      }
    },
    fetchPolicy: "cache-and-network"
  });

  const vehicleStatusResult = useQuery(ALL_SYSTEM_CODES_QUERY, {
    variables: {
      where: {
        Category: "Fleet",
        CodeName: "VehicleStatus",
        CodeDescription_not: "Terminated"
      }
    }
  });

  const [createWorkOrder, createWorkOrderData] = useMutation(
    CREATE_WORK_ORDER_MUTATION,
    {
      refetchQueries: ["ALL_WORK_ORDER_VIEW_QUERY", "SINGLE_WORK_ORDER_QUERY"]
    }
  );

  const [updateWorkOrder, updateWorkOrderData] = useMutation(
    UPDATE_WORK_ORDER_MUTATION,
    {
      refetchQueries: ["SINGLE_WORK_ORDER_QUERY", "ALL_WORK_ORDER_VIEW_QUERY"]
    }
  );

  const [deleteWorkOrderLabor, deleteWorkOrderLaborData] = useMutation(
    DELETE_WORK_ORDER_LABOR_MUTATION,
    {
      refetchQueries: ["ALL_WORK_ORDER_LABORS_QUERY"]
    }
  );

  const [updateAllOrderLabors, updateAllOrderLaborsData] = useMutation(
    UPDATE_ALL_WORK_ORDER_LABORS_MUTATION,
    {
      refetchQueries: ["ALL_WORK_ORDER_LABORS_QUERY"]
    }
  );

  const [deleteWorkOrderPart, deleteWorkOrderPartData] = useMutation(
    DELETE_WORK_ORDER_PART_MUTATION,
    {
      refetchQueries: ["ALL_WORK_ORDER_PARTS_QUERY"]
    }
  );

  const [updateAllOrderParts, updateAllOrderPartsData] = useMutation(
    UPDATE_ALL_WORK_ORDER_PARTS_MUTATION,
    {
      refetchQueries: ["ALL_WORK_ORDER_PARTS_QUERY"]
    }
  );

  const [createVehicleInspection] = useMutation(
    CREATE_VEHICLE_INSPECTION_MUTATION,
    {
      refetchQueries: ["ALL_VEHICLE_INSPECTIONS_QUERY"]
    }
  );

  const [inputs, setInputs] = useState({ ...INITIAL_STATE });
  const [Labors, setLabors] = useState(null);
  const [Parts, setParts] = useState(null);

  const odometerRequired = useOdometerRequired(
    inputs.WOTypeRecIdRef,
    inputs.ItemCatgRecIdRef,
    inputs.VehicleTypeRecIdRef
  );

  const isInspectionServiceWorkOrder = useMemo(
    () =>
      Array.isArray(inspectionServiceSystemCodeData) &&
      inputs.WOTypeRecIdRef &&
      inspectionServiceSystemCodeData[0].RecId ===
        parseInt(inputs.WOTypeRecIdRef, 10),
    [inspectionServiceSystemCodeData, inputs]
  );

  const getQuantityDimOnHand = useCallback(
    async item => {
      if (item.ItemRecIdRef) {
        const where = {
          ItemRecIdRef: parseInt(
            item.ItemRecIdRef.value
              ? item.ItemRecIdRef.value
              : item.ItemRecIdRef
          ),
          InvDim1RecIdRef:
            item.InvDim1RecIdRef && typeof item.InvDim1RecIdRef === "object"
              ? parseInt(item.InvDim1RecIdRef.value)
              : item.InvDim1RecIdRef && parseInt(item.InvDim1RecIdRef),
          InvDim2RecIdRef:
            item.InvDim2RecIdRef && typeof item.InvDim2RecIdRef === "object"
              ? parseInt(item.InvDim2RecIdRef.value)
              : item.InvDim2RecIdRef && parseInt(item.InvDim2RecIdRef),
          InvDim3RecIdRef:
            item.InvDim3RecIdRef && typeof item.InvDim3RecIdRef === "object"
              ? parseInt(item.InvDim3RecIdRef.value)
              : item.InvDim3RecIdRef && parseInt(item.InvDim3RecIdRef),
          CompanyLocRecIdRef:
            workOrderData?.data?.WorkOrder?.CompanyLocation?.RecId
        };
        const DimOHData = await client.query({
          query: ALL_INVENTORY_ON_HAND_QUERY,
          variables: {
            where
          },
          fetchPolicy: "network-only"
        });
        return (
          (DimOHData &&
            DimOHData.data &&
            DimOHData.data.InventoryOnHands &&
            Array.isArray(DimOHData.data.InventoryOnHands) &&
            DimOHData.data.InventoryOnHands.length > 0 &&
            DimOHData.data.InventoryOnHands.reduce(
              (a, b) => a + (b.QtyOnHand || 0),
              0
            )) ||
          "0"
        );
      }
    },
    [client, workOrderData]
  );

  const getFiltersForDIMParts = useCallback(
    async parts => {
      const newParts = await Promise.all(
        parts.map(async part => {
          if (part.ItemRecIdRef) {
            const oHAvailables = await client.query({
              query: ALL_INVENTORY_ON_HAND_QUERY,
              variables: {
                where: {
                  ItemRecIdRef:
                    part.ItemRecIdRef && parseInt(part.ItemRecIdRef.value),
                  CompanyLocRecIdRef:
                    workOrderData?.data?.WorkOrder?.CompanyLocation?.RecId
                }
              }
            });
            if (
              oHAvailables &&
              oHAvailables.data &&
              oHAvailables.data.InventoryOnHands &&
              Array.isArray(oHAvailables.data.InventoryOnHands) &&
              oHAvailables.data.InventoryOnHands.length > 0
            ) {
              const InvDim1Availables = oHAvailables.data.InventoryOnHands.filter(
                iOH => iOH.InvDim1
              ).map(iOH => iOH.InvDim1.RecId);
              const InvDim2Availables = oHAvailables.data.InventoryOnHands.filter(
                iOH => iOH.InvDim2
              ).map(iOH => iOH.InvDim2.RecId);
              const InvDim3Availables = oHAvailables.data.InventoryOnHands.filter(
                iOH => iOH.InvDim3
              ).map(iOH => iOH.InvDim3.RecId);
              return {
                ...part,
                InvDim1Availables,
                InvDim2Availables,
                InvDim3Availables
              };
            }
          }
          return part;
        })
      );
      setParts(newParts);
    },
    [client, workOrderData]
  );

  const getPartsWithQtyOH = useCallback(
    async WorkOrderParts => {
      setLoading(true);

      const newParts = await Promise.all(
        WorkOrderParts.map(async part => {
          const {
            Item,
            BillFlag,
            Cost,
            MarkUpCost,
            Active,
            LineNote,
            Quantity,
            RecId,
            LineNo,
            WorkOrderRecIdRef,
            InvDim1,
            InvDim2,
            InvDim3
          } = part;
          if (!Item) return {};
          const OHData = await client.query({
            query: ALL_INVENTORY_ON_HAND_QUERY,
            variables: {
              where: {
                ItemRecIdRef: parseInt(Item.RecId),
                CompanyLocRecIdRef:
                  workOrderData?.data?.WorkOrder?.CompanyLocation?.RecId
              }
            },
            fetchPolicy: "network-only"
          });
          const DimOH = await getQuantityDimOnHand({
            ...Item,
            InvDim1RecIdRef: InvDim1 && {
              value: InvDim1.RecId,
              name: `InvDim1RecIdRef_${RecId}`,
              label: InvDim1.DimCode
            },
            InvDim2RecIdRef: InvDim2 && {
              value: InvDim2.RecId,
              name: `InvDim2RecIdRef_${RecId}`,
              label: InvDim2.DimCode
            },
            InvDim3RecIdRef: InvDim3 && {
              value: InvDim3.RecId,
              name: `InvDim3RecIdRef_${RecId}`,
              label: InvDim3.DimCode
            },
            ItemRecIdRef: parseInt(Item.RecId)
          });
          return {
            WorkOrderRecIdRef,
            LineNo,
            RecId,
            Quantity,
            LineNote,
            Active,
            BillFlag,
            MarkUpCost: formatCurrency(MarkUpCost),
            Cost: Cost ? formatCurrency(Cost) : formatCurrency(Item.StdCost),
            QtyOnHand:
              (OHData &&
                OHData.data &&
                OHData.data.InventoryOnHands &&
                Array.isArray(OHData.data.InventoryOnHands) &&
                OHData.data.InventoryOnHands.length > 0 &&
                OHData.data.InventoryOnHands.reduce(
                  (a, b) => a + (b.QtyOnHand || 0),
                  0
                )) ||
              "0",
            ItemCategoryRecIdRef: Item?.ItemCategory?.RecId,
            ItemRecIdRef: Item && {
              value: Item.RecId,
              name: "ItemRecIdRef",
              StdCost: Item.StdCost,
              listPrice: Item.ListPrice,
              label: `${Item.ItemNo}`
            },
            ItemDescriptionRecIdRef: Item && {
              value: Item.RecId,
              StdCost: Item.StdCost,
              listPrice: Item.ListPrice,
              name: "ItemDescriptionRecIdRef",
              label: `${Item.Description}`
            },
            DimOH,
            InvDim1RecIdRef: InvDim1 && {
              value: InvDim1.RecId,
              name: `InvDim1RecIdRef_${RecId}`,
              label: InvDim1.DimCode
            },
            InvDim2RecIdRef: InvDim2 && {
              value: InvDim2.RecId,
              name: `InvDim2RecIdRef_${RecId}`,
              label: InvDim2.DimCode
            },
            InvDim3RecIdRef: InvDim3 && {
              value: InvDim3.RecId,
              name: `InvDim3RecIdRef_${RecId}`,
              label: InvDim3.DimCode
            }
          };
        })
      );
      setParts(newParts);
      await getFiltersForDIMParts(newParts);
      setLoading(false);
    },
    [client, getFiltersForDIMParts, getQuantityDimOnHand, workOrderData]
  );

  useEffect(() => {
    const result = workOrderData?.data?.WorkOrder;
    if (result && !workOrderData.loading) {
      const {
        RecId,
        Odometer,
        ItemCatgRecIdRef,
        WODate,
        CompletedDate,
        Billable,
        Notes,
        LocRecIdRef,
        WOTypeRecIdRef,
        MechanicRecIdRef,
        WOStatusRecIdRef,
        VehicleRecIdRef,
        VendorRecIdRef,
        Vendor,
        Active,
        LaborApproved,
        PartsApproved,
        PostedDt,
        PostedBy,
        ApprovedBy,
        ApprovalDt,
        Vehicle,
        WorkOrder,
        Status
      } = result;
      setInputs({
        RecId,
        Odometer,
        ItemCatgRecIdRef,
        WODate: WODate && new Date(WODate).toISOString().slice(0, 10),
        CompletedDate:
          CompletedDate && new Date(CompletedDate).toISOString().slice(0, 10),
        Billable: Billable === "Y",
        Notes,
        Vehicle,
        LocRecIdRef,
        WOTypeRecIdRef,
        MechanicRecIdRef,
        WOStatusRecIdRef,
        Vendor,
        VendorRecIdRef: Vendor
          ? {
              value: Vendor.RecId,
              name: VendorRecIdRef,
              Vendor,
              label: `${Vendor.VendorNo} - ${Vendor.Name}`,
              otherLabel: `${Vendor.Name} - ${Vendor.VendorNo}`
            }
          : null,
        Active,
        LaborApproved,
        PartsApproved,
        PostedDt: PostedDt && new Date(PostedDt).toISOString().slice(0, 10),
        PostedBy,
        ApprovedBy,
        ApprovalDt:
          ApprovalDt && new Date(ApprovalDt).toISOString().slice(0, 10),
        VehicleRecIdRef,
        WorkOrder,
        Status
      });
    }
  }, [workOrderData]);

  useEffect(() => {
    if (
      !Labors &&
      workOrderLaborsData &&
      !workOrderLaborsData.loading &&
      workOrderLaborsData.data &&
      workOrderLaborsData.data.WorkOrderLabors &&
      workOrderData.data?.WorkOrder?.RecId &&
      Array.isArray(workOrderLaborsData.data.WorkOrderLabors)
    ) {
      if (
        workOrderLaborsData.data.WorkOrderLabors.length === 0 &&
        workOrderData.data.WorkOrder.Status.CodeDescription !== "Approved" &&
        workOrderData.data.WorkOrder.Status.CodeDescription !== "Posted" &&
        !workOrderData.data.WorkOrder.LaborApproved
      ) {
        setLabors([
          {
            WorkOrderRecIdRef: workOrderData.data.WorkOrder.RecId,
            LineNo: 1,
            RecId: `tg${uuidGenerator()}`,
            DateWorked:
              workOrderData.data.WorkOrder.WODate ||
              workOrderData.data.WorkOrder.CompletedDate ||
              new Date().getTime(),
            EmployeeRecIdRef: workOrderData.data.WorkOrder.MechanicRecIdRef,
            ItemCategoryRecIdRef: workOrderData.data.WorkOrder.ItemCatgRecIdRef,
            BillFlag: workOrderData.data.WorkOrder.Billable === "Y",
            Description: "",
            HoursWorked: "",
            BillHours: 0,
            LaborCostRate: workOrderData?.data?.WorkOrder?.Mechanic?.HourlyRate
              ? formatCurrency(workOrderData.data.WorkOrder.Mechanic.HourlyRate)
              : formatCurrency(0),
            BillingRate: formatCurrency(0),
            LineNote: "",
            Active: true,
            Taxable: false
          }
        ]);
      } else if (workOrderLaborsData.data.WorkOrderLabors.length > 0) {
        setLabors(
          workOrderLaborsData.data.WorkOrderLabors.map(labor => {
            const {
              WorkOrderRecIdRef,
              LineNo,
              RecId,
              DateWorked,
              EmployeeRecIdRef,
              ItemCategoryRecIdRef,
              BillFlag,
              Description,
              HoursWorked,
              BillHours,
              LaborCostRate,
              LineNote,
              Active,
              Taxable,
              BillingRate
            } = labor;
            return {
              WorkOrderRecIdRef,
              LineNo,
              RecId,
              DateWorked,
              EmployeeRecIdRef,
              ItemCategoryRecIdRef,
              BillFlag,
              Description,
              HoursWorked,
              BillHours,
              LineNote,
              Active,
              Taxable,
              LaborCostRate: formatCurrency(LaborCostRate),
              BillingRate: formatCurrency(BillingRate)
            };
          })
        );
      } else {
        setLabors([]);
      }
    }
    if (
      !Parts &&
      !workOrderPartsData.loading &&
      workOrderPartsData.data &&
      workOrderPartsData.data.WorkOrderParts &&
      Array.isArray(workOrderPartsData.data.WorkOrderParts) &&
      workOrderData.data?.WorkOrder?.RecId
    ) {
      if (
        workOrderPartsData.data.WorkOrderParts.length === 0 &&
        workOrderData.data.WorkOrder.Status.CodeDescription !== "Approved" &&
        workOrderData.data.WorkOrder.Status.CodeDescription !== "Posted" &&
        !workOrderData.data.WorkOrder.PartsApproved
      ) {
        setParts([
          {
            WorkOrderRecIdRef: workOrderData.data.WorkOrder.RecId,
            LineNo: 1,
            RecId: `tg${uuidGenerator()}`,
            Quantity: null,
            ItemRecIdRef: null,
            LineNote: "",
            Active: true,
            MarkUpCost: null,
            Cost: null,
            QtyOnHand: null,
            // ItemCategoryRecIdRef:
            //   workOrderData?.data?.WorkOrder.ItemCatgRecIdRef,
            ItemCategoryRecIdRef: null,
            // workOrderData?.data?.WorkOrder.ItemCatgRecIdRef,
            BillFlag: workOrderData.data.WorkOrder.Billable === "Y"
          }
        ]);
      } else if (workOrderPartsData.data.WorkOrderParts.length > 0) {
        getPartsWithQtyOH(workOrderPartsData.data.WorkOrderParts);
      } else {
        setParts([]);
      }
    }
    if (
      user &&
      user.RecId &&
      isMechanic &&
      !inputs.RecId &&
      !inputs.MechanicRecIdRef &&
      user.EmployeeRecIdRef
    ) {
      setInputs(prev => ({
        ...prev,
        MechanicRecIdRef: user.EmployeeRecIdRef
      }));
    }
    if (SystemCodes && !inputs.WOStatusRecIdRef) {
      const status = SystemCodes?.data?.[0]?.RecId;
      if (status) {
        setInputs({ ...inputs, WOStatusRecIdRef: status });
      }
    }
  }, [
    Parts,
    Labors,
    workOrderData,
    workOrderLaborsData,
    workOrderPartsData,
    inputs.RecId,
    inputs.MechanicRecIdRef,
    inputs.ItemCatgRecIdRef,
    inputs.Billable,
    editing,
    user,
    isMechanic,
    SystemCodes,
    inputs,
    getPartsWithQtyOH,
    getFiltersForDIMParts
  ]);

  // Function to reorder Work Order Labors items
  const reorderLaborItems = newInputs => {
    const currInputs = newInputs.map((item, i) => ({
      ...item,
      LineNo: i + 1
    }));
    setLabors(currInputs);
  };

  // Inserting a new line from Labor Tab
  const insertAfterLaborItem = index => () => {
    if (
      Labors.find(
        d =>
          d.BillHours === "" ||
          d.HoursWorked === "" ||
          d.BillingRate === "" ||
          d.LaborCostRate === "" ||
          d.ItemCatgRecIdRef === "" ||
          d.EmployeeRecIdRef === ""
      )
    ) {
      toast.error("Fields required");
    } else {
      const currInputs = Labors;
      currInputs.splice(index + 1, 0, {
        WorkOrderRecIdRef: inputs.RecId,
        LineNo: index + 2,
        RecId: `tg${uuidGenerator()}`,
        DateWorked:
          inputs.WODate || inputs.CompletedDate || new Date().getTime(),
        EmployeeRecIdRef: inputs.MechanicRecIdRef,
        ItemCategoryRecIdRef: inputs.ItemCatgRecIdRef,
        Description: "",
        HoursWorked: "",
        BillHours: 0,
        LaborCostRate:
          formatCurrency(
            workOrderData?.data?.WorkOrder?.Mechanic?.HourlyRate
          ) || "",
        BillingRate: formatCurrency(0),
        LineNote: "",
        Active: true,
        Taxable: false
      });
      reorderLaborItems(currInputs);
    }
  };

  // Function to reorder Work Order Parts items
  const reorderPartItems = newInputs => {
    const currInputs = newInputs.map((item, i) => ({
      ...item,
      LineNo: i + 1
    }));
    setParts(currInputs);
  };

  // Inserting a new line from Part Tab
  const insertAfterPartItem = index => () => {
    if (Parts.find(d => !d.ItemRecIdRef || !d.Quantity)) {
      toast.error("Fields required.");
    } else {
      const currInputs = Parts;
      currInputs.splice(index + 1, 0, {
        WorkOrderRecIdRef: inputs.RecId,
        // ItemCategoryRecIdRef: workOrderData?.data?.WorkOrder.ItemCatgRecIdRef,
        ItemCategoryRecIdRef: "",
        LineNo: index + 2,
        RecId: `tg${uuidGenerator()}`,
        Quantity: null,
        ItemRecIdRef: null,
        LineNote: "",
        Active: true,
        BillFlag: inputs.Billable,
        MarkUpCost: null
      });
      reorderPartItems(currInputs);
    }
  };

  const onChange = e => {
    const updates = { ...inputs };

    if (e.value && !e.target) {
      updates[e.name] = e;
      setSaved(false);
      if (e.name === "VendorRecIdRef") {
        updates.Vendor = e.Vendor;
      }
    } else {
      if (
        saved &&
        ((inputs.RecId &&
          e.target.name === "Vehicle" &&
          parseInt(inputs.VehicleRecIdRef, 10) ===
            parseInt(e.target.value?.RecId, 10)) ||
          !inputs.RecId)
      ) {
        setSaved(true);
      } else {
        setSaved(false);
      }
      if (e.target.name === "CompletedDate" && !inputs.VehicleRecIdRef) {
        toast.warning("Please assign a vehicle before completing.");
        return;
      }
      if (
        e.target.name === "CompletedDate" &&
        Labors.some(labor => !labor.HoursWorked)
      ) {
        toast.warning("Please complete the hours field on the Labor tab.");
        return;
      }
      if (
        e.target.name === "CompletedDate" &&
        !isSameDay(new Date(e.target.value), new Date(inputs.WODate)) &&
        new Date(inputs.WODate) > new Date(e.target.value)
      ) {
        toast.warning("Completed date must be after scheduled date.");
        return;
      }
      const value =
        e.target.type === "checkbox" ? e.target.checked : e.target.value;
      updates[e.target.name] = value;
    }
    if (e.target.name === "WOTypeRecIdRef") {
      updates.ItemCatgRecIdRef = null;
    }
    setInputs(prev => ({ ...prev, ...updates }));
  };
  const { data: tractorVehicleTypeSystemCode } = useSystemCodes({
    Category: "Fleet",
    CodeName: "VehicleType",
    CodeDescription: "Tractor"
  });

  const { data: inspectionServiceSystemCode } = useSystemCodes({
    Category: "Fleet",
    CodeName: "WOType",
    CodeDescription: "Inspection Service"
  });

  const isTractor = useMemo(
    () =>
      Array.isArray(tractorVehicleTypeSystemCode) &&
      inputs?.Vehicle?.VehicleTypeRecIdRef &&
      tractorVehicleTypeSystemCode[0].RecId ===
        parseInt(inputs.Vehicle.VehicleTypeRecIdRef, 10),
    [tractorVehicleTypeSystemCode, inputs]
  );

  const showOdometerError =
    (isTractor || odometerRequired) &&
    (!inputs.Odometer ||
      (inputs.Odometer &&
        inputs?.Vehicle?.Odometer &&
        parseInt(inputs.Odometer, 10) < parseInt(inputs.Vehicle.Odometer, 10)));

  const handleSaveMainInformation = async (e, goTo) => {
    if (e) {
      e.preventDefault();
    }

    try {
      if (inspectionServiceSystemCode[0].RecId === inputs.WOTypeRecIdRef) {
        const { data: ItemCategoryData } = await client.query({
          query: SINGLE_ITEM_CATEGORY_QUERY,
          variables: {
            where: {
              RecId: parseInt(inputs.ItemCatgRecIdRef, 10)
            }
          }
        });

        const inspectionCdRecIdRef =
          ItemCategoryData?.ItemCategory?.InspectionCdRecIdRef;

        if (inspectionCdRecIdRef) {
          const { data: VehicleInspectionsMasterData } = await client.query({
            query: ALL_VEHICLE_INSPECTIONS_MASTER_QUERY,
            variables: {
              where: {
                VehicleRecIdRef: parseInt(inputs.VehicleRecIdRef, 10),
                InspectionTypeRecIdRef: parseInt(inspectionCdRecIdRef, 10)
              }
            },
            fetchPolicy: "network-only"
          });

          await Promise.all(
            VehicleInspectionsMasterData?.VehicleInspectionsMasters.map(
              async vehicleInspection => {
                const {
                  data: { Inspections }
                } = await client.query({
                  query: ALL_INSPECTIONS_QUERY,
                  variables: {
                    where: {
                      InspecCdRecIdRef: parseInt(
                        vehicleInspection.InspectionTypeRecIdRef
                      ),
                      VehicleTypeRecIdRef: parseInt(
                        inputs.Vehicle.VehicleTypeRecIdRef
                      ),
                      OdometerReq: true
                    },
                    first: 1
                  },
                  fetchPolicy: "network-only"
                });
                if (Inspections.length && inputs.Odometer) {
                  throw new OdometerError(
                    "An updated Odometer reading is mandatory.  Please enter."
                  );
                }
                return vehicleInspection;
              }
            )
          );
        }
      }
      if (showOdometerError) {
        throw new OdometerError(
          "Odometer must be greater than current odometer reading."
        );
      }
      if (showOdometerError && inputs.Odometer === "") {
        throw new OdometerError(`Odometer reading can't be blank`);
      }
      setLoading(true);
      setSaved(false);
      const data = {
        VehicleRecIdRef:
          inputs.VehicleRecIdRef && parseInt(inputs.VehicleRecIdRef),
        VendorRecIdRef:
          inputs.VendorRecIdRef && parseInt(inputs.VendorRecIdRef.value),
        Odometer: inputs.Odometer ? parseInt(inputs.Odometer) : null,
        WOTypeRecIdRef:
          inputs.WOTypeRecIdRef && parseInt(inputs.WOTypeRecIdRef),
        ItemCatgRecIdRef: inputs.ItemCatgRecIdRef
          ? parseInt(inputs.ItemCatgRecIdRef)
          : null,
        WODate: inputs.WODate ? getUTCFormattedDate(inputs.WODate) : null,
        CompletedDate: inputs.CompletedDate
          ? getUTCFormattedDate(inputs.CompletedDate)
          : null,
        Billable: inputs.Billable ? "Y" : "N",
        Notes: inputs.Notes,
        LocRecIdRef: inputs.LocRecIdRef ? parseInt(inputs.LocRecIdRef) : null,
        MechanicRecIdRef:
          inputs.MechanicRecIdRef && inputs.MechanicRecIdRef !== ""
            ? parseInt(inputs.MechanicRecIdRef)
            : null,
        WOStatusRecIdRef: inputs.WOStatusRecIdRef
          ? parseInt(inputs.WOStatusRecIdRef)
          : null,
        Active: inputs.Active,
        PromisedDate: inputs.PromisedDate
          ? getUTCFormattedDate(inputs.PromisedDate)
          : null
      };
      let newWorkOrderData = {};
      if (!inputs.RecId) {
        newWorkOrderData = await createWorkOrder({
          variables: {
            data
          }
        });
      } else {
        newWorkOrderData = await updateWorkOrder({
          variables: {
            data,
            where: { RecId: parseInt(inputs.RecId) }
          }
        });
      }
      if (!inputs.RecId) {
        toast.success("Work order created successfully.");
      } else {
        toast.success("Work order updated successfully.");
      }
      setLoading(false);
      setSaved(true);

      if (goTo) {
        if (
          (!inputs?.WODate || !inputs?.MechanicRecIdRef) &&
          (goTo.includes("parts") || goTo.includes("labor"))
        ) {
          toast.error(
            "A Scheduled Date and Mechanic are required before navigating to another tab."
          );
        } else {
          history.push(goTo);
        }
      } else {
        const destPath = `/fleet/work-orders/${inputs.RecId ||
          newWorkOrderData.data.createWorkOrder.RecId}/main-information`;
        if (location.pathname !== destPath) {
          history.push(
            `/fleet/work-orders/${inputs.RecId ||
              newWorkOrderData.data.createWorkOrder.RecId}/main-information`
          );
        }
      }

      if (
        inputs.WODate &&
        isSameDay(getUTCFormattedDate(inputs.WODate), new Date())
      ) {
        const { data: VehicleOutOfServicesData } = await client.query({
          query: ALL_VEHICLE_OUT_OF_SERVICES_QUERY,
          variables: {
            where: {
              VehicleRecIdRef: parseInt(inputs.VehicleRecIdRef, 10),
              OutOfServiceDate_lt: endOfDay(new Date())
            },
            orderByMulti: ["CreatedDateTime_DESC"],
            first: 1
          },
          fetchPolicy: "network-only"
        });

        const oosRecords = VehicleOutOfServicesData?.VehicleOutOfServices || [];
        const inShopServiceStatusRecId = vehicleStatusResult?.data?.SystemCodes?.find(
          x => x.CodeId === "O"
        )?.RecId;

        if (
          !oosRecords.every(
            oos => oos.StatusRecIdRef === inShopServiceStatusRecId
          )
        ) {
          setModalOpen(
            true,
            <Confirmation
              message="Would you like to update the vehicle status to out of service?"
              onConfirm={() => {
                setModalOpen(false, "");
                setModalOpen(
                  true,
                  <VehicleOutOfServiceModal
                    {...{
                      id: null,
                      vehicleId: inputs.VehicleRecIdRef,
                      onComplete: () => setModalOpen(false),
                      WorkOrder: inputs
                    }}
                  />
                );
              }}
            />
          );
        }
      }
      if (
        !workOrderData.data?.WorkOrder?.CompletedDate &&
        inputs.CompletedDate
      ) {
        setModalOpen(
          true,
          <Confirmation
            message="Would you like to update the vehicle status to available?"
            onConfirm={() => {
              setModalOpen(false, "");
              setModalOpen(
                true,
                <VehicleOutOfServiceModal
                  {...{
                    id: null,
                    vehicleId: inputs.VehicleRecIdRef,
                    onComplete: () => setModalOpen(false),
                    WorkOrder: inputs
                  }}
                />
              );
            }}
          />
        );
      }
    } catch (err) {
      setLoading(false);
      if (err instanceof OdometerError) {
        toast.error(err.message);
      } else {
        toast.error(
          `Error ${inputs.RecId ? "updating" : "creating"} Work Order`
        );
      }
    }
  };

  const handleDeleteMainInformation = async e => {
    e.preventDefault();
    try {
      setEditing(false);
      const performDelete = async () => {
        setLoading(true);
        setModalOpen(false, "");
        await client.mutate({
          mutation: DELETE_WORK_ORDER_MUTATION,
          variables: {
            where: {
              RecId: parseInt(inputs.RecId)
            }
          },
          refetchQueries: [
            "SINGLE_WORK_ORDER_QUERY",
            "ALL_WORK_ORDER_VIEW_QUERY"
          ]
        });

        setLoading(false);
        toast.success(`#WO # ${id} Deleted`);
        history.push("/fleet/work-orders");
        if (
          inputs.Vehicle &&
          inputs.Vehicle.Status &&
          inputs.Vehicle.Status.CodeId === "A"
        ) {
          setModalOpen(
            true,
            <Confirmation
              message="Would you like to update the Out of Service Code now?"
              onConfirm={() => {
                setModalOpen(false, "");
                setModalOpen(
                  true,
                  <VehicleOutOfServiceModal
                    {...{
                      id: null,
                      vehicleId: inputs.VehicleRecIdRef,
                      onComplete: () => setModalOpen(false),
                      WorkOrder: inputs
                    }}
                  />
                );
              }}
            />
          );
        } else if (
          inputs.Vehicle &&
          inputs.Vehicle.Status &&
          inputs.Vehicle.Status.CodeId !== "A"
        ) {
          setModalOpen(
            true,
            <Confirmation
              message="Do you wanto to change it back to Available?"
              onConfirm={() => {
                const activeStatus = vehicleStatusResult?.data?.SystemCodes?.find(
                  s => s.CodeId === "A"
                );
                setModalOpen(false, "");
                setModalOpen(
                  true,
                  <VehicleOutOfServiceModal
                    {...{
                      id: null,
                      vehicleId: inputs.VehicleRecIdRef,
                      onComplete: () => setModalOpen(false),
                      WorkOrder: inputs,
                      StatusByDefault: activeStatus.RecId,
                      CommentByDefault: `#WO # ${id} Deleted`
                    }}
                  />
                );
              }}
            />
          );
        }
      };
      setModalOpen(
        true,
        <Confirmation
          message="Are you sure you want to delete?"
          onConfirm={performDelete}
        />
      );
    } catch (err) {
      toast.error("Error deleting Work Order.");
    }
  };

  // Removing one specific item from Work Order Labors list
  const removeLaborItem = async (index, item) => {
    const currInputs = Labors;
    if (typeof item.RecId === "string") {
      // If the item to delete is not registered in database yet
      currInputs.splice(index, 1);
      reorderLaborItems(currInputs);
    } else {
      await deleteWorkOrderLabor({
        variables: {
          where: {
            RecId: item.RecId
          }
        }
      });
      currInputs.splice(index, 1);
      await reorderLaborItems(currInputs);
      if (currInputs.length === 0) {
        await handleSaveMainInformation(false, false);
      }
      toast.success("Work Order Labor deleted successfully.");
    }
  };

  // Handle Change function for Labor Tab Inputs
  const handleChangeLabors = () => async e => {
    if (Labors && workOrderData && workOrderData.data) {
      setSaved(false);
      let { value } = e.target;
      const keyName = e.target.name.split("_")[0];
      let newData = Labors.map(i => {
        if (
          e.target.value &&
          (keyName.includes("LaborCostRate") || keyName.includes("BillingRate"))
        ) {
          // If it's a currency input
          const unmaskedValue = unmaskCurrency(e.target.value);
          value = formatCurrency(unmaskedValue);
        } else if (
          e.target.value &&
          (keyName.includes("HoursWorked") ||
            keyName.includes("ItemCategoryRecIdRef") ||
            keyName.includes("EmployeeRecIdRef") ||
            keyName.includes("BillHours"))
        ) {
          value = parseFloat(value) || "0";
        }
        if (
          i.RecId === e.target.name.split("_")[1] ||
          i.RecId === parseInt(e.target.name.split("_")[1])
        ) {
          if (
            keyName.includes("HoursWorked") &&
            workOrderData.data.WorkOrder.Billable === "Y"
          ) {
            // Only one way change of Hours Worked and Bill Hours Input
            return {
              ...i,
              [keyName]: value,
              BillHours: value
            };
          }
          if (
            keyName.includes("LaborCostRate") &&
            workOrderData.data.WorkOrder.Billable === "Y"
          ) {
            // Only one way change of Labor Cost Rate and Billing Rate Input
            return {
              ...i,
              [keyName]: value,
              BillingRate: value
            };
          }
          return {
            ...i,
            [keyName]:
              keyName === "DateWorked" ? new Date(value).getTime() : value
          };
        }
        return i;
      });
      let employeeData = null;
      if (keyName.includes("EmployeeRecIdRef")) {
        const { name } = e.target;
        employeeData = await client.query({
          query: SINGLE_EMPLOYEE_QUERY,
          variables: {
            where: {
              RecId: parseInt(e.target.value) || null
            }
          },
          fetchPolicy: "network-only"
        });
        if (
          employeeData.data &&
          !employeeData.loading &&
          employeeData.data.Employee
        ) {
          newData = Labors.map(i => {
            if (
              i.RecId === name.split("_")[1] ||
              i.RecId === parseInt(name.split("_")[1])
            ) {
              if (keyName.includes("EmployeeRecIdRef")) {
                return {
                  ...i,
                  EmployeeRecIdRef: employeeData?.data?.Employee?.RecId,
                  LaborCostRate: formatCurrency(
                    employeeData?.data?.Employee?.HourlyRate
                  )
                };
              }
            }
            return i;
          });
        } else {
          newData = Labors.map(i => {
            if (
              i.RecId === name.split("_")[1] ||
              i.RecId === parseInt(name.split("_")[1])
            ) {
              if (keyName.includes("EmployeeRecIdRef")) {
                return {
                  ...i,
                  EmployeeRecIdRef: "",
                  LaborCostRate: "$0.00"
                };
              }
            }
            return i;
          });
        }
      }
      setLabors(newData);
    }
  };
  // Saving Work Order Labors
  const handleSaveLaborsInformation = async (e, goTo = false) => {
    if (e) {
      e.preventDefault();
    }
    try {
      // Front-end validations
      if (
        Labors.find(
          d =>
            d.BillHours === "" ||
            d.HoursWorked === "" ||
            d.BillingRate === "" ||
            d.LaborCostRate === "" ||
            d.ItemCatgRecIdRef === "" ||
            d.EmployeeRecIdRef === ""
        )
      ) {
        setLoading(false);
        toast.error("Fields required.");
      } else {
        setLoading(true);
        // Clearing and sending the request
        const copyLabors = _.cloneDeep(Labors);
        const dataToSend = copyLabors.map(i => {
          const copy = i;
          delete copy.__typename;
          if (typeof copy.RecId === "string") {
            delete copy.RecId;
          }
          return {
            ...copy,
            LaborCostRate: unmaskCurrency(copy.LaborCostRate),
            BillingRate: unmaskCurrency(copy.BillingRate),
            BillDate: copy.BillDate ? getUTCFormattedDate(copy.BillDate) : null,
            DateWorked: copy.DateWorked
              ? getUTCFormattedDate(copy.DateWorked)
              : null
          };
        });
        await updateAllOrderLabors({
          variables: {
            data: dataToSend
          }
        });
        setLabors(null);
        setSaved(true);
        setLoading(false);
        toast.success("Work orders labors updated successfully.");
        if (goTo) {
          history.push(goTo);
        } else {
          history.push(`/fleet/work-orders/${inputs.RecId}/labor`);
        }
      }
    } catch (error) {
      setLoading(false);
      toast.error("Error on update Work Orders Labors.");
    }
  };

  // Function to update Approved Labors
  const approveLabors = async () => {
    try {
      if (
        workOrderData &&
        workOrderData.data &&
        workOrderData.data.WorkOrder &&
        user
      ) {
        // Front-end validations
        if (
          Labors.find(
            d =>
              d.BillHours === "" ||
              d.HoursWorked === "" ||
              d.BillingRate === "" ||
              d.LaborCostRate === "" ||
              d.ItemCatgRecIdRef === "" ||
              d.EmployeeRecIdRef === ""
          )
        ) {
          toast.error("Fields required.");
        } else {
          setLoading(true);
          if (!saved) {
            handleSaveLaborsInformation(false);
          }
          const data = {
            LaborApproved: true,
            ApprovalDt: new Date(),
            ApprovalUserRecIdRef: parseInt(user.RecId)
          };
          await updateWorkOrder({
            variables: {
              data,
              where: { RecId: parseInt(inputs.RecId) }
            }
          });
          setSaved(true);
          setLoading(false);
          toast.success("Work Order Labor approved successfully");
          setLabors(null);
          history.push(`/fleet/work-orders/${inputs.RecId}/labor`);
        }
      }
    } catch (error) {
      toast.error("Error updating Work Order.");
    }
  };

  // Removing one specific item from Work Order Parts list
  const removePartItem = async (index, item) => {
    const currInputs = Parts;
    if (typeof item.RecId === "string") {
      // If the item to delete is not registered in database yet
      currInputs.splice(index, 1);
      reorderPartItems(currInputs);
    } else {
      await deleteWorkOrderPart({
        variables: {
          where: {
            RecId: item.RecId
          }
        }
      });
      currInputs.splice(index, 1);
      await reorderPartItems(currInputs);
      if (currInputs.length === 0) {
        await handleSaveMainInformation(false, false);
      }
      toast.success("Work Order Part deleted successfully.");
    }
  };

  // Handle Change function for Part Tab Inputs
  const handleChangeParts = () => async e => {
    if (Parts) {
      setSaved(false);
      const keyName = e.target ? e.target.name.split("_")[0] : e.name;
      const newData = await Promise.all(
        Parts.map(async i => {
          let { value } = e.target ? e.target : e.value;
          if (
            e.value &&
            !e.target &&
            (keyName.includes("ItemRecIdRef") ||
              keyName.includes("ItemDescriptionRecIdRef") ||
              keyName.includes("InvDim1RecIdRef") ||
              keyName.includes("InvDim2RecIdRef") ||
              keyName.includes("InvDim3RecIdRef"))
          ) {
            if (
              keyName.includes("ItemDescriptionRecIdRef") &&
              (i.RecId === e.name.split("_")[1] ||
                i.RecId === parseInt(e.name.split("_")[1]))
            ) {
              const OHData = await client.query({
                query: ALL_INVENTORY_ON_HAND_QUERY,
                variables: {
                  where: {
                    ItemRecIdRef: parseInt(e.value),
                    CompanyLocRecIdRef:
                      workOrderData?.data?.WorkOrder?.CompanyLocation?.RecId
                  }
                },
                fetchPolicy: "network-only"
              });
              const DimOH = await getQuantityDimOnHand({
                ...i,
                ItemRecIdRef: e.value
              });
              return {
                ...i,
                MarkUpCost: formatCurrency(e.listPrice),
                ItemDescriptionRecIdRef: {
                  ...e,
                  label: e.label.replace(`- ${e.otherLabel}`, "")
                },
                QtyOnHand:
                  (OHData &&
                    OHData.data &&
                    OHData.data.InventoryOnHands &&
                    Array.isArray(OHData.data.InventoryOnHands) &&
                    OHData.data.InventoryOnHands.length > 0 &&
                    OHData.data.InventoryOnHands.reduce(
                      (a, b) => a + (b.QtyOnHand || 0),
                      0
                    )) ||
                  "0",
                ItemCategoryRecIdRef: e.ItemCategory?.RecId,
                Cost:
                  companySettings.InventoryCostMethod.CodeDescription ===
                  "Average Cost"
                    ? formatCurrency(e.AvgCost)
                    : formatCurrency(e.StdCost),
                ItemRecIdRef: {
                  ...e,
                  label: e.otherLabel
                },
                DimOH,
                InvDim1RecIdRef: null,
                InvDim2RecIdRef: null,
                InvDim3RecIdRef: null
              };
            }
            if (
              keyName.includes("ItemRecIdRef") &&
              (i.RecId === e.name.split("_")[1] ||
                i.RecId === parseInt(e.name.split("_")[1]))
            ) {
              const OHData = await client.query({
                query: ALL_INVENTORY_ON_HAND_QUERY,
                variables: {
                  where: {
                    ItemRecIdRef: parseInt(e.value),
                    CompanyLocRecIdRef:
                      workOrderData?.data?.WorkOrder?.CompanyLocation?.RecId
                  }
                },
                fetchPolicy: "network-only"
              });
              const DimOH = await getQuantityDimOnHand({
                ...i,
                ItemRecIdRef: e.value
              });
              return {
                ...i,
                ItemRecIdRef: {
                  ...e,
                  label: e.label.replace(`- ${e.otherLabel}`, "")
                },
                MarkUpCost: formatCurrency(e.listPrice),
                Cost:
                  companySettings.InventoryCostMethod.CodeDescription ===
                  "Average Cost"
                    ? formatCurrency(e.AvgCost)
                    : formatCurrency(e.StdCost),
                QtyOnHand:
                  (OHData &&
                    OHData.data &&
                    OHData.data.InventoryOnHands &&
                    Array.isArray(OHData.data.InventoryOnHands) &&
                    OHData.data.InventoryOnHands.length > 0 &&
                    OHData.data.InventoryOnHands.reduce(
                      (a, b) => a + (b.QtyOnHand || 0),
                      0
                    )) ||
                  "0",
                ItemCategoryRecIdRef: e.ItemCategory?.RecId,
                ItemDescriptionRecIdRef: {
                  ...e,
                  label: e.otherLabel
                },
                DimOH,
                InvDim1RecIdRef: null,
                InvDim2RecIdRef: null,
                InvDim3RecIdRef: null
              };
            }
            if (
              e.name &&
              (keyName.includes("InvDim1RecIdRef") ||
                keyName.includes("InvDim2RecIdRef") ||
                keyName.includes("InvDim3RecIdRef")) &&
              (i.RecId === e.name.split("_")[1] ||
                i.RecId === parseInt(e.name.split("_")[1]))
            ) {
              const DimOH = await getQuantityDimOnHand({
                ...i,
                [keyName.split("_")[0]]: e.value === "0" ? null : e.value
              });
              return {
                ...i,
                [keyName.split("_")[0]]:
                  e.value === "0"
                    ? null
                    : {
                        ...e,
                        value: e.value
                      },
                DimOH
              };
            }
          } else if (
            i.RecId === e.target.name.split("_")[1] ||
            i.RecId === parseInt(e.target.name.split("_")[1])
          ) {
            if (
              keyName.includes("ListPrice") ||
              keyName.includes("MarkUpCost")
            ) {
              // If it's a currency input
              const unmaskedValue = unmaskCurrency(e.target.value);
              value = formatCurrency(unmaskedValue);
            } else if (keyName.includes("ItemCategoryRecIdRef")) {
              return {
                ...i,
                ItemRecIdRef: null,
                MarkUpCost: formatCurrency(0),
                Cost: formatCurrency(0),
                QtyOnHand: 0,
                ItemCategoryRecIdRef: e.target.value,
                ItemDescriptionRecIdRef: null,
                InvDim1RecIdRef: null,
                InvDim2RecIdRef: null,
                InvDim3RecIdRef: null,
                DimOH: 0
              };
            } else {
              value =
                e.target.type === "checkbox"
                  ? e.target.checked
                  : e.target.value;
            }
            return {
              ...i,
              [keyName]: value
            };
          }
          return i;
        })
      );
      // this is for validate dimensions available depends on some dropdowns selected
      setParts(newData);
      if (
        e.value &&
        !e.target &&
        (keyName.includes("ItemRecIdRef") ||
          keyName.includes("ItemDescriptionRecIdRef"))
      ) {
        await getFiltersForDIMParts(newData);
      }
    }
  };

  // Saving Work Order Parts
  const handleSavePartsInformation = async (e, goTo = false) => {
    if (e) {
      e.preventDefault();
    }
    try {
      // Front-end validations
      if (Parts.find(d => !d.ItemRecIdRef || !d.Quantity)) {
        setLoading(false);
        toast.error("Fields required.");
      } else {
        const warning = Parts.find(i => parseInt(i.DimOH) < i.Quantity);
        const warningDimOH = Parts.find(
          i => parseInt(i.DimOH) === 0 && i.QtyOnHand > 0
        );
        if (warning) {
          toast.warning(
            "This quantity will send the Dimension Onhand negative.  This WO Part will be saved but not posted.  Please notify Supervisor"
          );
        }
        if (warningDimOH) {
          toast.warning(
            "Please check your dimension selection as this selection has 0 quantity on hand and there is a quantity and another dimension group."
          );
        }
        setLoading(true);
        // Clearing and sending the request
        const copyParts = _.cloneDeep(Parts);
        const dataToSend = copyParts.map(i => {
          const copy = i;
          delete copy.__typename;
          delete copy.ItemDescriptionRecIdRef;
          delete copy.Item;
          delete copy.Category;
          delete copy.QtyOnHand;
          delete copy.DimOH;
          delete copy.InvDim1Availables;
          delete copy.InvDim2Availables;
          delete copy.InvDim3Availables;
          if (typeof copy.RecId === "string") {
            delete copy.RecId;
          }
          copy.ItemCategoryRecIdRef =
            copy.ItemCategoryRecIdRef && parseInt(copy.ItemCategoryRecIdRef);
          copy.Quantity = parseInt(i.Quantity);
          copy.ItemRecIdRef = parseInt(i.ItemRecIdRef.value);
          copy.MarkUpCost = unmaskCurrency(i.MarkUpCost);
          copy.Cost = unmaskCurrency(i.Cost);
          copy.BillDate = i.BillDate ? getUTCFormattedDate(i.BillDate) : null;
          if (copy.InvDim1RecIdRef) {
            copy.InvDim1RecIdRef = copy.InvDim1RecIdRef.value;
          }
          if (copy.InvDim2RecIdRef) {
            copy.InvDim2RecIdRef = copy.InvDim2RecIdRef.value;
          }
          if (copy.InvDim3RecIdRef) {
            copy.InvDim3RecIdRef = copy.InvDim3RecIdRef.value;
          }
          return copy;
        });
        await updateAllOrderParts({
          variables: {
            data: dataToSend
          }
        });
        setParts(null);
        setSaved(true);
        setLoading(false);
        toast.success("Work Order Parts updated successfully.");
        if (goTo) {
          history.push(goTo);
        } else {
          history.push(`/fleet/work-orders/${inputs.RecId}/parts`);
        }
      }
    } catch (error) {
      setLoading(false);
      toast.error("Error on update Work Order Parts.");
    }
  };

  // Function to update Approved Parts
  const approveParts = async () => {
    try {
      if (
        workOrderData &&
        workOrderData.data &&
        workOrderData.data.WorkOrder &&
        user
      ) {
        if (Parts.find(d => !d.ItemRecIdRef || !d.Quantity)) {
          setLoading(false);
          toast.error("Fields required.");
        } else if (
          Parts.find(
            part => parseInt(part.DimOH, 10) < parseInt(part.Quantity, 10)
          )
        ) {
          toast.error(
            "Work Order quantity will send On-hand Negative.  Please see supervisor"
          );
        } else {
          setLoading(true);
          if (!saved) {
            handleSavePartsInformation();
          }
          const data = {
            PartsApproved: true,
            ApprovalDt: new Date(),
            ApprovalUserRecIdRef: parseInt(user.RecId)
          };
          await updateWorkOrder({
            variables: {
              data,
              where: { RecId: parseInt(inputs.RecId) }
            }
          });
          setLoading(false);
          toast.success("Work Order Parts approved successfully");
          setParts(null);
          history.push(`/fleet/work-orders/${inputs.RecId}/parts`);
        }
      }
    } catch (error) {
      toast.error("Error updating Work Order.");
    }
  };

  const postWorkOrder = async () => {
    try {
      if (user) {
        if (
          Parts.find(
            part => parseInt(part.DimOH, 10) < parseInt(part.Quantity, 10)
          )
        ) {
          toast.error(
            "Work Order quantity will send On-hand Negative.  Please see supervisor"
          );
        } else {
          setLoading(true);
          const data = {
            PostedDt: new Date(),
            PostedByRecIdRef: parseInt(user.RecId)
          };
          await updateWorkOrder({
            variables: {
              data,
              where: { RecId: parseInt(inputs.RecId) }
            },
            refetchQueries: [
              "SINGLE_WORK_ORDER_QUERY",
              "ALL_WORK_ORDER_VIEW_QUERY"
            ]
          });
          if (inspectionServiceSystemCode[0].RecId === inputs.WOTypeRecIdRef) {
            const { data: ItemCategoryData } = await client.query({
              query: SINGLE_ITEM_CATEGORY_QUERY,
              variables: {
                where: {
                  RecId: parseInt(inputs.ItemCatgRecIdRef, 10)
                }
              }
            });

            const inspectionCdRecIdRef =
              ItemCategoryData?.ItemCategory?.InspectionCdRecIdRef;

            if (inspectionCdRecIdRef) {
              const {
                data: { VehicleInspections }
              } = await client.query({
                query: ALL_VEHICLE_INSPECTIONS_QUERY,
                variables: {
                  where: {
                    VehicleRecIdRef: parseInt(inputs.VehicleRecIdRef, 10),
                    InspectionTypeRecIdRef: parseInt(inspectionCdRecIdRef, 10)
                  },
                  first: 1,
                  orderByMulti: ["RecId_DESC"]
                },
                fetchPolicy: "network-only"
              });

              const lastInspectionService = VehicleInspections?.[0];
              const lastInspectionDate = new Date(
                lastInspectionService?.InspectionDate
              );
              const today = new Date();
              const daysInterval =
                getDaysDiffBetweenDates(today, lastInspectionDate) || 0;

              const getMechanicFullName = async mechanicRecIdRef => {
                if (mechanicRecIdRef) {
                  const { data: MechanicData } = await client.query({
                    query: SINGLE_EMPLOYEE_QUERY,
                    variables: {
                      where: {
                        RecId: parseInt(mechanicRecIdRef, 10)
                      }
                    }
                  });
                  if (MechanicData?.Employee) {
                    return [
                      MechanicData.Employee.LastName,
                      MechanicData.Employee.FirstName
                    ]
                      .filter(Boolean)
                      .join(", ");
                  }
                }
                return null;
              };

              const mechanicFullName = await getMechanicFullName(
                inputs.MechanicRecIdRef
              );

              await createVehicleInspection({
                variables: {
                  data: {
                    WorkOrderRecIdRef: parseInt(inputs.RecId),
                    MechanicEmployeeRecIdRef: parseInt(inputs.MechanicRecIdRef),
                    VehicleRecIdRef: parseInt(inputs.VehicleRecIdRef),
                    InspectionTypeRecIdRef: parseInt(inspectionCdRecIdRef, 10),
                    InspectedBy: mechanicFullName,
                    OdometerReading: inputs.Odometer,
                    MileageInterval:
                      lastInspectionService &&
                      lastInspectionService.OdometerReading !== 0
                        ? inputs.Odometer -
                          lastInspectionService.OdometerReading
                        : null,
                    DaysInterval:
                      daysInterval > 0
                        ? Math.round(daysInterval)
                        : Math.round(daysInterval * -1),
                    InspectionDate: inputs.CompletedDate
                  }
                }
              });
            }
          }
          setLoading(false);
          setSaved(true);
          toast.success("Work Order posted successfully");
          if (saved) {
            history.push(`/fleet/work-orders/${inputs.RecId}/main-information`);
          }
        }
      }
    } catch (error) {
      toast.error("Error updating Work Order.");
    }
  };

  const unapproveWorkOrder = async () => {
    try {
      if (user) {
        setLoading(true);
        const data = {
          PartsApproved: false,
          LaborApproved: false,
          ApprovalDt: null,
          ApprovalUserRecIdRef: null
        };
        await updateWorkOrder({
          variables: {
            data,
            where: { RecId: parseInt(inputs.RecId, 10) }
          }
        });
        setLoading(false);
        setSaved(true);
        history.push(`/fleet/work-orders/${inputs.RecId}/main-information`);
        toast.success("Work Order unapproved successfully");
      }
    } catch (error) {
      setLoading(false);
      toast.error("Error updating Work Order.");
    }
  };

  const printWorkOrder = async () => {
    await handleSaveMainInformation(false, false);
    if (inputs.RecId)
      window.open(`${BASE_URL}&Param=WorkOrder&ParamValue=${inputs.RecId}`);
  };

  const openInspectionList = async () => {
    await handleSaveMainInformation(false, false);
    window.open(
      `${REACT_APP_REPORT_BASE_URL}/ReportViewer/ReportViewer.aspx?Path=Inspection&Param=WORecIdRef&ParamValue=${id}`
    );
  };

  const handleVoidClick = () => {
    setModalOpen(
      true,
      <VoidWorkOrderModal
        WorkOrder={inputs}
        onComplete={() => setModalOpen(false, "")}
      />
    );
  };
  const toggleEditing = () => setEditing(!editing);

  useEffect(() => {
    (async () => {
      if (inputs.VehicleRecIdRef) {
        const { data: VehicleData } = await client.query({
          query: SINGLE_VEHICLE_QUERY,
          variables: {
            where: { RecId: parseInt(inputs.VehicleRecIdRef, 10) }
          }
        });
        if (VehicleData.Vehicle) {
          setInputs(prev => ({ ...prev, Vehicle: VehicleData.Vehicle }));
        }
      }
    })();
  }, [client, inputs.VehicleRecIdRef]);

  const isInsidePostingPeriod = useCanPostWorkOrder(inputs.CompletedDate);

  if (
    loading ||
    createWorkOrderData?.loading ||
    updateWorkOrderData?.loading ||
    workOrderData?.loading ||
    updateAllOrderLaborsData?.loading ||
    deleteWorkOrderLaborData?.loading ||
    workOrderPartsData?.loading ||
    updateAllOrderPartsData?.loading ||
    deleteWorkOrderPartData?.loading ||
    workOrderLaborsData?.loading ||
    vehicleStatusResult.loading
  )
    return <Loading />;

  const getActionButtons = () => {
    if (location.pathname.includes("main-information")) {
      return (
        <Field kind="group">
          {canPost &&
            workOrderData?.data?.WorkOrder?.Status?.CodeDescription !==
              "Posted" && (
              <Control>
                <Button color="danger" size="small" onClick={handleVoidClick}>
                  <Icon>
                    <FontAwesomeIcon icon="exclamation-circle" />
                  </Icon>
                  <span>Void</span>
                </Button>
              </Control>
            )}
          {isInspectionServiceWorkOrder && (
            <Control>
              <Button color="info" size="small" onClick={openInspectionList}>
                <Icon>
                  <FontAwesomeIcon icon="file-export" />
                </Icon>
                <span>Inspection List</span>
              </Button>
            </Control>
          )}
          {inputs.RecId && (
            <Control>
              <Button color="link" size="small" onClick={printWorkOrder}>
                <Icon>
                  <FontAwesomeIcon icon="print" />
                </Icon>
                <span>Print</span>
              </Button>
            </Control>
          )}
          {inputs.VehicleRecIdRef && inputs.RecId && (
            <Control>
              <Button
                color="primary"
                size="small"
                onClick={() =>
                  setModalOpen(
                    true,
                    <VehicleOutOfServiceModal
                      {...{
                        id: null,
                        vehicleId: inputs.VehicleRecIdRef,
                        onComplete: () => setModalOpen(false),
                        WorkOrder: inputs
                      }}
                    />
                  )
                }
              >
                <Icon>
                  <FontAwesomeIcon icon="truck" />
                </Icon>
                <span>Update Vehicle Status</span>
              </Button>
            </Control>
          )}
          {inputs.RecId && (
            <React.Fragment>
              {workOrderData.data?.WorkOrder?.Status?.CodeDescription ===
                "Approved" &&
                (workOrderData.data?.WorkOrder?.LaborApproved ||
                  workOrderData.data?.WorkOrder?.PartsApproved) &&
                canPost && (
                  <React.Fragment>
                    <Control>
                      <Button
                        color="success"
                        size="small"
                        onClick={postWorkOrder}
                        disabled={!isInsidePostingPeriod}
                      >
                        <Icon>
                          <FontAwesomeIcon icon="check" />
                        </Icon>
                        <span>Post</span>
                      </Button>
                    </Control>
                    <Control>
                      <Button
                        color="danger"
                        size="small"
                        onClick={unapproveWorkOrder}
                      >
                        <Icon>
                          <FontAwesomeIcon icon="ban" />
                        </Icon>
                        <span>Unapprove</span>
                      </Button>
                    </Control>
                  </React.Fragment>
                )}
              {inputs &&
                inputs.Status &&
                inputs.Status.CodeDescription !== "Posted" && (
                  <React.Fragment>
                    <Control>
                      <Button
                        color="primary"
                        size="small"
                        onClick={toggleEditing}
                      >
                        <Icon>
                          <FontAwesomeIcon icon="edit" />
                        </Icon>
                        <span>Edit</span>
                      </Button>
                    </Control>
                    <Control>
                      <Button
                        color="danger"
                        size="small"
                        onClick={handleDeleteMainInformation}
                      >
                        <Icon>
                          <FontAwesomeIcon icon="trash" />
                        </Icon>
                        <span>Delete</span>
                      </Button>
                    </Control>
                  </React.Fragment>
                )}
            </React.Fragment>
          )}
          {!(
            (inputs &&
              inputs.Status &&
              inputs.Status.CodeDescription === "Approved") ||
            (inputs &&
              inputs.Status &&
              inputs.Status.CodeDescription === "Posted")
          ) && (
            <Control>
              <Button
                color="success"
                size="small"
                onClick={handleSaveMainInformation}
              >
                <Icon>
                  <FontAwesomeIcon icon="save" />
                </Icon>
                <span>Save</span>
              </Button>
            </Control>
          )}
        </Field>
      );
    }
    if (location.pathname.includes("labor") && Labors) {
      return (
        <Field kind="group">
          <Control>
            <Button
              color="link"
              disabled={
                inputs.LaborApproved ||
                inputs.Status?.CodeDescription === "Approved"
              }
              size="small"
              onClick={insertAfterLaborItem(
                Labors.length === 0 ? Labors.length : Labors.length - 1
              )}
            >
              <Icon>
                <FontAwesomeIcon icon="plus" />
              </Icon>
              <span>Add Labor</span>
            </Button>
          </Control>

          {!isMechanic &&
            inputs.RecId &&
            inputs &&
            inputs.Status &&
            inputs.Status.CodeDescription === "Completed" &&
            !inputs.LaborApproved &&
            inputs.Status.CodeDescription !== "Approved" && (
              <Control>
                <Button color="primary" size="small" onClick={approveLabors}>
                  <Icon>
                    <FontAwesomeIcon icon="check" />
                  </Icon>
                  <span>Approve</span>
                </Button>
              </Control>
            )}
          {inputs.RecId &&
            inputs &&
            inputs.Status &&
            (inputs.LaborApproved ||
              inputs.Status.CodeDescription === "Approved") && (
              <Control>
                <Button disabled color="primary" size="small">
                  <Icon>
                    <FontAwesomeIcon icon="check" />
                  </Icon>
                  <span>Approved</span>
                </Button>
              </Control>
            )}
          {!(
            (inputs &&
              inputs.Status &&
              inputs.Status.CodeDescription === "Approved") ||
            (inputs &&
              inputs.Status &&
              inputs.Status.CodeDescription === "Posted")
          ) &&
            !inputs.LaborApproved && (
              <Control>
                <Button
                  color="success"
                  size="small"
                  onClick={handleSaveLaborsInformation}
                >
                  <Icon>
                    <FontAwesomeIcon icon="save" />
                  </Icon>
                  <span>Save</span>
                </Button>
              </Control>
            )}
        </Field>
      );
    }
    if (location.pathname.includes("parts") && Parts) {
      return (
        <Field kind="group">
          <Control>
            <Button
              color="link"
              disabled={
                inputs.PartsApproved ||
                inputs.Status?.CodeDescription === "Approved"
              }
              size="small"
              onClick={insertAfterPartItem(
                Parts.length === 0 ? Parts.length : Parts.length - 1
              )}
            >
              <Icon>
                <FontAwesomeIcon icon="plus" />
              </Icon>
              <span>Add Part</span>
            </Button>
          </Control>

          {!isMechanic &&
            inputs &&
            inputs.RecId &&
            inputs.Status &&
            inputs.Status.CodeDescription === "Completed" &&
            !inputs.PartsApproved &&
            inputs.Status.CodeDescription !== "Approved" && (
              <Control>
                <Button color="primary" size="small" onClick={approveParts}>
                  <Icon>
                    <FontAwesomeIcon icon="check" />
                  </Icon>
                  <span>Approve</span>
                </Button>
              </Control>
            )}
          {inputs &&
            inputs.RecId &&
            inputs.Status &&
            (inputs.PartsApproved ||
              inputs.Status.CodeDescription === "Approved") && (
              <Control>
                <Button disabled color="primary" size="small">
                  <Icon>
                    <FontAwesomeIcon icon="check" />
                  </Icon>
                  <span>Approved</span>
                </Button>
              </Control>
            )}
          {!(
            (inputs &&
              inputs.Status &&
              inputs.Status.CodeDescription === "Approved") ||
            (inputs &&
              inputs.Status &&
              inputs.Status.CodeDescription === "Posted")
          ) &&
            !inputs.PartsApproved && (
              <Control>
                <Button
                  color="success"
                  disabled={!inputs.RecId && !inputs.PartsApproved}
                  size="small"
                  onClick={handleSavePartsInformation}
                >
                  <Icon>
                    <FontAwesomeIcon icon="save" />
                  </Icon>
                  <span>Save</span>
                </Button>
              </Control>
            )}
        </Field>
      );
    }
  };
  const changeTab = tab => () => {
    if (!saved) {
      if (location.pathname.includes("labor")) {
        handleSaveLaborsInformation(
          false,
          `/fleet/work-orders/${inputs.RecId}/${tab}`
        );
      }
      if (location.pathname.includes("parts")) {
        handleSavePartsInformation(
          false,
          `/fleet/work-orders/${inputs.RecId}/${tab}`
        );
      }
      if (location.pathname.includes("main-information")) {
        handleSaveMainInformation(
          false,
          `/fleet/work-orders/${inputs.RecId}/${tab}`
        );
      }
    } else if (
      (!workOrderData?.data?.WorkOrder?.WODate ||
        !workOrderData?.data?.WorkOrder?.Mechanic) &&
      (tab === "parts" || tab === "labor")
    ) {
      toast.error(
        "A Scheduled Date and Mechanic are required before navigating to another tab."
      );
    } else {
      history.push(`/fleet/work-orders/${inputs.RecId}/${tab}`);
    }
  };

  return (
    <div className="work-order-page">
      <header className="wo-page-head">
        <div className="wo-page-head-start">
          <Title size={4} style={{ marginBottom: 0 }}>
            Work Order
            {inputs.RecId ? ` #${inputs.WorkOrder}` : ""}
          </Title>
          {inputs && inputs.RecId && inputs.Status && (
            <Tag
              className={`white-tag tag-${inputs.Status.CodeDescription}`}
              style={{ fontSize: 12 }}
            >
              {inputs.Status.CodeDescription}
            </Tag>
          )}

          {inputs.PostedBy && (
            <Tag style={{ fontSize: 12 }}>
              Posted by: {inputs.PostedBy.FirstName} {inputs.PostedBy.LastName}{" "}
              {inputs.PostedDt && moment(inputs.PostedDt).format("MM/DD/YYYY")}
            </Tag>
          )}
          {inputs.ApprovedBy && (
            <Tag style={{ fontSize: 12 }}>
              Approved by: {inputs.ApprovedBy.FirstName}{" "}
              {inputs.ApprovedBy.LastName}{" "}
              {inputs.ApprovalDt &&
                moment(inputs.ApprovalDt).format("MM/DD/YYYY")}
            </Tag>
          )}
        </div>
        <div className="wo-page-head-end">{getActionButtons()}</div>
      </header>
      <Tab.Group kind="boxed">
        <Tab
          active={location.pathname.includes("main-information")}
          as={Button}
          style={{
            cursor:
              location.pathname.includes("labor") &&
              Labors &&
              Labors.find(l => l.EmployeeRecIdRef === "")
                ? "no-drop"
                : "pointer"
          }}
          onClick={
            location.pathname.includes("labor") &&
            Labors &&
            Labors.find(l => l.EmployeeRecIdRef === "")
              ? null
              : changeTab("main-information")
          }
        >
          <span>Main information</span>
        </Tab>
        {workOrderData?.data?.WorkOrder && (
          <React.Fragment>
            <Tab
              active={location.pathname.includes("labor")}
              as={Button}
              style={{
                cursor:
                  location.pathname.includes("main-information") &&
                  (inputs.MechanicRecIdRef === "" && !saved
                    ? "no-drop"
                    : "pointer")
              }}
              onClick={
                location.pathname.includes("main-information") &&
                inputs.MechanicRecIdRef === "" &&
                !saved
                  ? null
                  : changeTab("labor")
              }
            >
              <span>Labor</span>
            </Tab>
            <Tab
              active={location.pathname.includes("parts")}
              as={Button}
              style={{
                cursor:
                  (location.pathname.includes("labor") &&
                    (Labors && Labors.find(l => l.EmployeeRecIdRef === "")
                      ? "no-drop"
                      : "pointer")) ||
                  (location.pathname.includes("main-information") &&
                    (inputs.MechanicRecIdRef === "" && !saved
                      ? "no-drop"
                      : "pointer"))
              }}
              onClick={
                (location.pathname.includes("labor") &&
                  (Labors && Labors.find(l => l.EmployeeRecIdRef === "")
                    ? null
                    : changeTab("parts"))) ||
                (location.pathname.includes("main-information") &&
                  (inputs.MechanicRecIdRef === "" && !saved
                    ? null
                    : changeTab("parts")))
              }
            >
              <span>Parts</span>
            </Tab>
          </React.Fragment>
        )}
      </Tab.Group>
      <Switch>
        <Route path="/fleet/work-orders/:id/main-information">
          <MainInformation
            VSSystemCode={
              VSSystemCode && VSSystemCode.data && VSSystemCode.data[0]
            }
            companySettings={companySettings}
            editing={
              editing &&
              !(
                inputs.Status?.CodeDescription === "Approved" ||
                inputs.Status?.CodeDescription === "Posted"
              )
            }
            inputs={inputs}
            isPosted={
              workOrderData.data?.WorkOrder?.Status?.CodeDescription ===
              "Posted"
            }
            vehicleStatus={[
              null,
              ...vehicleStatusResult?.data?.SystemCodes?.map(s => s.RecId)
            ]}
            onChange={onChange}
          />
        </Route>
        <Route path="/fleet/work-orders/:id/labor">
          <Labor
            Items={Labors}
            WorkOrder={workOrderData?.data?.WorkOrder}
            companySettings={companySettings}
            handleChange={handleChangeLabors}
            loading={
              loading ||
              !editing ||
              inputs.Status?.CodeDescription === "Approved" ||
              inputs.Status?.CodeDescription === "Posted"
            }
            removeRow={removeLaborItem}
          />
        </Route>
        <Route path="/fleet/work-orders/:id/parts">
          <Part
            Parts={Parts}
            PrimaryStorageDimension={companySettings?.PrimaryStorageDimension}
            SecondaryStorageDimension={
              companySettings?.SecondaryStorageDimension
            }
            TertiaryStorageDimension={companySettings?.TertiaryStorageDimension}
            UsePrimaryStorageDimension={
              companySettings?.UsePrimaryStorageDimension
            }
            UseSecondaryStorageDimension={
              companySettings?.UseSecondaryStorageDimension
            }
            UseTertiaryStorageDimension={
              companySettings?.UseTertiaryStorageDimension
            }
            WorkOrder={workOrderData?.data?.WorkOrder}
            companySettings={companySettings}
            handleChange={handleChangeParts}
            insertAfter={insertAfterPartItem}
            loading={
              loading ||
              !editing ||
              inputs.Status?.CodeDescription === "Approved" ||
              inputs.Status?.CodeDescription === "Posted"
            }
            removeRow={removePartItem}
          />
        </Route>
      </Switch>
    </div>
  );
};
WorkOrderPage.propTypes = {};

export default WorkOrderPage;
